'use client';

import classNames from 'classnames';
import React, { useState } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import { ChevronRight, Diagram2 } from 'react-bootstrap-icons';

import { SPACER } from '@/lib/constants';

import styles from '@/styles/PropertyLineage.module.scss';

export interface PropertyLineageRowsProps {
  /** Rendered property label (the row's <th> content). */
  label: React.ReactNode
  /** Extra class for the label cell, to match the host table's styling. */
  labelClassName?: string
  /** Rendered simplified value cell content (a truncated set of values). */
  values: React.ReactNode
  /** Values hidden beyond the truncated set; rendered as a "N more…" link that
   * opens the panel rather than expanding inline. */
  moreCount?: number
  /** Distinct datasets backing this property — the "N sources" count. */
  sourceCount: number
  /** Columns the value cell should span (factsheet uses 4, card tables 1). */
  valueColSpan: number
  /** Server-rendered lineage panel, revealed on expand. */
  panel: React.ReactNode
  defaultOpen?: boolean
}

/** A factsheet property row whose provenance unfolds inline.
 *
 * Reach for this instead of a plain `[sources]` link when statement-level
 * lineage is available: the trigger becomes a source count that expands the
 * statements behind the value in place, rather than sending the reader to the
 * separate raw-data explorer. Renders the trigger row plus a collapsible panel
 * row; keep the heavy, type-aware rendering (values, dataset links, dates) on
 * the server and pass it in as `values`/`panel`. */
export function PropertyLineageRows({ label, labelClassName, values, moreCount = 0, sourceCount, valueColSpan, panel, defaultOpen = false }: PropertyLineageRowsProps) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((o) => !o);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };
  const openPanel = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpen(true);
  };
  const onMoreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPanel(e);
    }
  };

  return (
    <>
      <tr
        className={styles.triggerRow}
        onClick={toggle}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <th className={classNames(styles.propLabel, labelClassName)}>{label}</th>
        <td colSpan={valueColSpan}>
          {values}
          {moreCount > 0 && !open && (
            <>
              {SPACER}
              <span
                className={styles.moreLink}
                role="button"
                tabIndex={0}
                onClick={openPanel}
                onKeyDown={onMoreKeyDown}
              >
                {moreCount} more…
              </span>
            </>
          )}
        </td>
        <td className={styles.sourceCell}>
          <span className={styles.trigger}>
            <Diagram2 className={styles.triggerIcon} />
            {sourceCount}&nbsp;{sourceCount === 1 ? 'source' : 'sources'}
            <ChevronRight className={classNames(styles.chevron, { [styles.chevronOpen]: open })} />
          </span>
        </td>
      </tr>
      <tr aria-hidden={!open}>
        <td colSpan={valueColSpan + 2} className={styles.panelCell}>
          <Collapse in={open}>
            <div>
              <div className={styles.panelInner}>{panel}</div>
            </div>
          </Collapse>
        </td>
      </tr>
    </>
  );
}
